import { MOOD_SCALE } from '../lib/constants';
import { useI18n } from '../i18n';
import { Bars, Suppressed } from './DataBits';

// The mood split as the server allows it (0020, decision 15 Oct 2026):
// - 'faces': the five faces with their counts;
// - 'bands': no face holds a single answer, so the faces are merged into
//   three groups (Struggling+Not great / Okay / Good+Great);
// - 'hidden': even a group would point at one answer; only the mean and the
//   count (shown by the caller) remain.
// Results from before 0020 carry no mood_mode and always show the faces.
export type MoodShape = {
  total: number;
  mood_mode?: 'faces' | 'bands' | 'hidden';
  mood_counts?: Record<string, number> | null;
  mood_bands?: { low: number; mid: number; high: number } | null;
};

export function MoodSplit({ result, labelledBy }: { result: MoodShape; labelledBy?: string }) {
  const { t } = useI18n();
  const mode = result.mood_mode ?? 'faces';

  if (mode === 'hidden') {
    return <Suppressed title={t['r4.moodHiddenTitle']}>{t['r4.moodHiddenBody']}</Suppressed>;
  }
  if (mode === 'bands' && result.mood_bands) {
    const b = result.mood_bands;
    return (
      <>
        <Bars
          labelledBy={labelledBy}
          total={result.total}
          data={[
            { key: 'high', emoji: '😊🙂', label: t['r4.band.high'], count: b.high },
            { key: 'mid', emoji: '😐', label: t['r4.band.mid'], count: b.mid },
            { key: 'low', emoji: '😕😔', label: t['r4.band.low'], count: b.low },
          ]}
        />
        <p className="bars-note">{t['r4.bandsNote']}</p>
      </>
    );
  }
  const counts = result.mood_counts ?? {};
  return (
    <Bars
      labelledBy={labelledBy}
      total={result.total}
      data={[...MOOD_SCALE].reverse().map((m) => ({
        key: String(m.value),
        emoji: m.emoji,
        label: t[`mood.${m.value}` as 'mood.1'],
        count: counts[String(m.value)] ?? 0,
      }))}
    />
  );
}
